import { useState } from 'react';
import {
  CurrencyFormat,
  Icon,
  NoFound,
  Search,
  Select,
  Spinner,
  Table,
  TableRow,
} from '@/components/generic';
import { cn } from '@/lib/utils';
import { CommissionStatus } from '@/types/referral';
import { SelectOption } from '@/types/select';
import { copyToClipboard } from '@/utils';
import { useGetReferralCommissions } from '@package/smart-wallet/hooks';
import { ethers } from 'ethers';

// Commission status with color classifications
const COMMISSION_STATUS_COLORS = {
  PROCESSING: { label: 'Processing', color: 'label-gray' },
  READY: { label: 'Ready', color: 'label-green' },
  CLAIMING: { label: 'Claiming', color: 'label-yellow' },
  PAID: { label: 'Paid', color: 'label-blue' },
  CANCELLED: { label: 'Cancelled', color: 'label-red' },
} as const;

// Status filter options
const statusFilterOptions: SelectOption[] = [
  { id: 0, name: 'All Status', value: '' },
  { id: 1, name: 'Processing', value: 'PROCESSING' },
  { id: 2, name: 'Ready', value: 'READY' },
  { id: 3, name: 'Claiming', value: 'CLAIMING' },
  { id: 4, name: 'Paid', value: 'PAID' },
  { id: 5, name: 'Cancelled', value: 'CANCELLED' },
];

// Level filter options
const levelFilterOptions: SelectOption[] = [
  { id: 0, name: 'All Levels', value: '' },
  { id: 1, name: 'Level 1', value: '1' },
  { id: 2, name: 'Level 2', value: '2' },
];

const tableHead = [
  'Commission ID',
  'Amount',
  'Level',
  'Percentage',
  'Status',
  'Transaction',
  'Date',
];

// Helper function to format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CommissionsTable = () => {
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [selectedStatusFilter, setSelectedStatusFilter] =
    useState<SelectOption>(statusFilterOptions[0]);
  const [selectedLevelFilter, setSelectedLevelFilter] = useState<SelectOption>(
    levelFilterOptions[0],
  );

  const {
    isLoading,
    records: commissionsData,
    total,
  } = useGetReferralCommissions({
    pageNum: currentPage,
    pageSize: pageSize,
    status: selectedStatusFilter.value
      ? (selectedStatusFilter.value as CommissionStatus)
      : undefined,
    level: selectedLevelFilter.value
      ? parseInt(selectedLevelFilter.value as string)
      : undefined,
  });

  // Filter commissions based on search (client-side)
  const filteredCommissions = (commissionsData || []).filter(
    (commission: any) => {
      const matchesSearch =
        search === '' ||
        String(commission.id || '')
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        String(commission.amount || '')
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        String(commission.transactionId || '')
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        COMMISSION_STATUS_COLORS[
          commission.status as keyof typeof COMMISSION_STATUS_COLORS
        ]?.label
          .toLowerCase()
          .includes(search.toLowerCase());

      return matchesSearch;
    },
  );

  // Calculate pagination info
  const totalPages = Math.ceil((total || 0) / pageSize);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  // Handle pagination
  const handleNextPage = () => {
    if (hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (hasPrevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className='card'>
      <div className='flex items-center px-5 py-3 max-lg:px-3'>
        <div className='mr-auto text-h6 max-md:text-sub-title-2'>
          Commission History
        </div>
        <Search
          className='w-70 max-lg:w-64 max-md:hidden'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder='Search commissions...'
          isGray
        />
      </div>

      {/* Filters Section */}
      <div className='flex flex-wrap items-center gap-4 px-5 py-3 max-lg:px-3 border-b border-s-stroke2'>
        <div className='flex items-center gap-2'>
          <label className='text-sm font-medium text-t-secondary'>
            Status:
          </label>
          <Select
            className='w-48'
            value={selectedStatusFilter}
            onChange={(value) => {
              setSelectedStatusFilter(value);
              setCurrentPage(1); // Reset to first page when filter changes
            }}
            options={statusFilterOptions}
            placeholder='Select status'
          />
        </div>
        <div className='flex items-center gap-2'>
          <label className='text-sm font-medium text-t-secondary'>Level:</label>
          <Select
            className='w-48'
            value={selectedLevelFilter}
            onChange={(value) => {
              setSelectedLevelFilter(value);
              setCurrentPage(1); // Reset to first page when filter changes
            }}
            options={levelFilterOptions}
            placeholder='Select level'
          />
        </div>
        {/* Mobile search */}
        <Search
          className='w-full md:hidden'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder='Search commissions...'
          isGray
        />
      </div>

      {isLoading ? (
        <div className='flex items-center justify-center py-20'>
          <Spinner />
        </div>
      ) : filteredCommissions.length === 0 ? (
        <NoFound
          title='No commissions found'
          sub='You have not earned any commissions yet. Start referring friends to earn rewards!'
        />
      ) : (
        <div className='overflow-x-auto'>
          <Table
            cellsThead={tableHead.map((head) => (
              <th key={head}>{head}</th>
            ))}
          >
            {filteredCommissions.map((commission: any) => (
              <TableRow key={commission.id}>
                {/* Commission ID */}
                <td className='max-md:text-caption'>
                  <div className='flex items-center gap-2'>
                    <span className='text-sub-title-2 max-md:text-body-2'>
                      #{commission.id}
                    </span>
                    <button
                      onClick={() => copyToClipboard(String(commission.id))}
                      className='shrink-0 opacity-0 group-hover:opacity-100 transition-opacity'
                    >
                      <Icon
                        className='fill-t-tertiary hover:fill-t-primary transition-colors'
                        name='copy'
                      />
                    </button>
                  </div>
                </td>

                {/* Amount */}
                <td className='text-left text-sub-title-2 max-md:text-body-2 max-md:font-normal whitespace-nowrap'>
                  <CurrencyFormat
                    value={Number(ethers.formatUnits(commission.amount || 0))}
                    currency='$'
                  />
                </td>

                {/* Level */}
                <td>
                  <div className='inline-flex items-center gap-1'>
                    <div className='label label-blue whitespace-nowrap'>
                      Level {commission.level}
                    </div>
                  </div>
                </td>

                {/* Percentage */}
                <td className='text-sub-title-2 max-md:text-body-2'>
                  {commission.percentage}%
                </td>

                {/* Status */}
                <td>
                  <div className='inline-flex items-center gap-1'>
                    <div
                      className={cn(
                        'label whitespace-nowrap',
                        COMMISSION_STATUS_COLORS[
                          commission.status as keyof typeof COMMISSION_STATUS_COLORS
                        ]?.color || 'label-gray',
                      )}
                    >
                      {COMMISSION_STATUS_COLORS[
                        commission.status as keyof typeof COMMISSION_STATUS_COLORS
                      ]?.label || commission.status}
                    </div>
                  </div>
                </td>

                {/* Transaction ID */}
                <td className='max-md:text-caption'>
                  {commission.transactionId ? (
                    <div className='flex items-center gap-2'>
                      <span className='text-sm'>
                        {commission.transactionId}
                      </span>
                      <button
                        onClick={() =>
                          copyToClipboard(String(commission.transactionId))
                        }
                        className='shrink-0 opacity-0 group-hover:opacity-100 transition-opacity'
                      >
                        <Icon
                          className='fill-t-tertiary hover:fill-t-primary transition-colors'
                          name='copy'
                        />
                      </button>
                    </div>
                  ) : (
                    <span className='text-t-tertiary text-sm'>N/A</span>
                  )}
                </td>

                {/* Date */}
                <td className='max-md:text-caption w-40 whitespace-nowrap'>
                  {formatDate(commission.createdAt)}
                </td>
              </TableRow>
            ))}
          </Table>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && filteredCommissions.length > 0 && (
        <div className='flex items-center justify-between px-5 py-4 border-t border-s-stroke2 max-md:flex-col max-md:gap-3'>
          <div className='text-sm text-t-secondary'>
            Showing {(currentPage - 1) * pageSize + 1} to{' '}
            {Math.min(currentPage * pageSize, total)} of {total} commissions
          </div>
          <div className='flex items-center gap-2'>
            <button
              onClick={handlePrevPage}
              disabled={!hasPrevPage}
              className={cn(
                'px-4 py-2 rounded-lg border border-s-stroke2 transition-colors',
                hasPrevPage
                  ? 'hover:bg-b-surface2 cursor-pointer'
                  : 'opacity-50 cursor-not-allowed',
              )}
            >
              Previous
            </button>
            <div className='text-sm text-t-secondary'>
              Page {currentPage} of {totalPages}
            </div>
            <button
              onClick={handleNextPage}
              disabled={!hasNextPage}
              className={cn(
                'px-4 py-2 rounded-lg border border-s-stroke2 transition-colors',
                hasNextPage
                  ? 'hover:bg-b-surface2 cursor-pointer'
                  : 'opacity-50 cursor-not-allowed',
              )}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionsTable;
